import React, { useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  FooterAction,
  Icons,
} from '@ohif/ui-next';
import { useReportAssessment } from '../../hooks/useReportAssessment';
import FindingsAssessment from './FindingsAssessment';
import NoFindingsAssessment from './NoFindingsAssessment';
import { AssessmentDialogProps } from './types';
import { ANCHOR } from '../dialogAnchor';

/**
 * Radix dispatches this on the content node itself (FocusScope's onMountAutoFocus), so
 * `currentTarget` is the dialog, and FocusScope's `tabIndex: -1`
 * (@radix-ui/react-focus-scope/dist/index.mjs:127) is merged onto it via `asChild` —
 * which is what makes the container itself a legal focus target when every row is
 * answered.
 */
function focusFirstUnansweredVerdict(event: Event): void {
  const content = event.currentTarget as HTMLElement | null;

  if (!content) {
    return;
  }

  const target =
    content.querySelector<HTMLElement>('[data-sycai-verdict="unanswered"] [role="radio"]') ??
    content;

  event.preventDefault();
  target.focus();
}

/**
 * Rendered inline by DiagnosisButton, not through uiDialogService — DiagnosisCompleteDialog
 * still goes to a service (D3); this one needs `onOpenAutoFocus`/`onCloseAutoFocus` handlers
 * ManagedDialog.tsx does not expose per-instance.
 *
 * `modal={false}`, matching OrganReviewDialog.tsx's own D-14: Radix's modal mode wraps
 * DialogContent in `react-remove-scroll` (@radix-ui/react-dialog's DialogContentModal),
 * which blocks wheel/touch scroll on everything outside the dialog — including the
 * viewport behind it, where a reviewer needs to scroll slices while assessing a finding.
 * `modal={false}` renders DialogContentNonModal instead, which skips that lock. The
 * traded-away half is Radix's focus trap and `hideOthers()` aria-hidden screening; Escape
 * and overlay-click are already disabled below (D4), so a keyboard user cannot dismiss the
 * dialog by tabbing out, only reach it. Focus return is NOT among the things `modal` buys
 * either way, because Radix ties it to DialogTrigger and this dialog has none;
 * `returnFocusToTrigger` below is what actually delivers it.
 *
 * THREE PROPS MOVED IN 3.13 (M3 D-6). develop passed `hideOverlay`, `disableOutsideClick`
 * and `disableEscapeKey` to DialogContent. In 3.13 the equivalents are `showOverlay`,
 * `shouldCloseOnOverlayClick` and `shouldCloseOnEsc`, they live on the Dialog ROOT
 * (Dialog.tsx:21-40), and they reach DialogContent through context, which enforces them at
 * :104-113. Passed to DialogContent they would land on the DOM node as unknown attributes.
 *
 * Escape and overlay-click stay disabled (D4): a stray keypress must not discard a
 * reviewer's in-progress clinical verdicts. Keyboard users are not stranded — both footer
 * buttons and Radix's own X (Dialog.tsx:116-121) are real buttons and are now reachable.
 */
export default function AssessmentDialog({
  open,
  onOpenChange,
  studyInstanceUid,
  returnFocusRef,
}: AssessmentDialogProps) {
  // Stable, because useReportAssessment lists it in `submit`'s dependency array. Inline,
  // it was a new identity every render, so `submit`'s useCallback rebuilt every render
  // and memoised nothing.
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  const { rows, isNoFindings, isSubmitDisabled, setClassification, setDetection, submit } =
    useReportAssessment(studyInstanceUid, close);

  /**
   * `modal={false}` routes this through DialogContentNonModal's onCloseAutoFocus
   * (@radix-ui/react-dialog/dist/index.mjs:176-183), the same finding OrganReviewDialog.tsx
   * records: it calls this handler first, then falls back to
   * `context.triggerRef.current?.focus()` only if the event survives un-prevented.
   * `triggerRef` is populated only by DialogTrigger (:63), which this dialog does not
   * render, so that fallback would focus null regardless — this handler's
   * `preventDefault()` is what keeps it from running at all, and delivers focus to
   * `returnFocusRef` instead of stranding it on <body>.
   */
  const returnFocusToTrigger = useCallback(
    (event: Event) => {
      event.preventDefault();
      returnFocusRef.current?.focus();
    },
    [returnFocusRef]
  );

  /**
   * Guards the click rather than passing `disabled`, for the reason ToggleGroup.tsx:77-85
   * gives: a native `<button disabled>` leaves the tab order, so a study whose rows are
   * all already submitted would hide Submit's existence from a screen-reader user
   * entirely. `ActionProps.onClick` is required (FooterAction.tsx:11), so the handler
   * cannot simply be dropped when the button is inert.
   */
  const submitIfEnabled = useCallback(() => {
    if (!isSubmitDisabled) {
      submit();
    }
  }, [isSubmitDisabled, submit]);

  return (
    <Dialog
      open={open}
      modal={false}
      onOpenChange={onOpenChange}
      showOverlay={false}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
    >
      {/*
        M5.5 round 2. Same orange shell border as OrganReviewDialog.tsx, and from the same
        reference: develop gave both dialogs a byte-identical !border-primary-main className.
        border-input measured 1.205:1 against the viewer behind it and was invisible.
      */}
      <DialogContent
        className="border-primary border sm:max-h-[270px] sm:max-w-[400px]"
        style={ANCHOR}
        onOpenAutoFocus={focusFirstUnansweredVerdict}
        onCloseAutoFocus={returnFocusToTrigger}
        data-cy="sycai-assessment-dialog"
      >
        <DialogHeader>
          <DialogTitle>Report Assessment</DialogTitle>
          {/*
            Radix warns when DialogContent has neither a description nor
            aria-describedby={undefined} (@radix-ui/react-dialog/dist/index.mjs:302-311).
            The comment this replaces claimed the DialogTitle had silenced it; the title
            silences the OTHER warning, at :288-298. sr-only because the 270px box has no
            room and the copy adds nothing a sighted reader cannot see.
          */}
          <DialogDescription className="sr-only">
            Record a Yes or No verdict for each finding, then submit.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-[170px] flex-col gap-4 overflow-y-auto pt-2">
          {isNoFindings ? (
            <NoFindingsAssessment
              row={rows[0]}
              // Index 0: no-findings mode is exactly one row, the same one isNoFindings
              // tested, and it is the row rendered here.
              onDetectionChange={value => setDetection(0, value)}
            />
          ) : (
            <FindingsAssessment
              rows={rows}
              onClassificationChange={setClassification}
              onDetectionChange={setDetection}
            />
          )}
        </div>

        <FooterAction className="gap-3 pt-1 pb-2">
          {/*
            M5.5 D5 ruled this Secondary and NOT the Auxiliary dismiss the delete and upload
            dialogs took: Save & Close persists the verdicts, so a transparent ghost beside
            Submit would advertise it as a cancel. It is a peer action and keeps its fill.

            M5.5 R-J/R-K, on this dialog's own reference: develop's AssessmentDialog footer is
            byte-identical to its OrganReviewDialog's. See that file's Save & Close for why the
            hover fill must travel with the 60%, and why M5.4 D8 is superseded here.
          */}
          <FooterAction.Secondary
            className="bg-primary/60 hover:bg-primary/100 flex-1 text-sm hover:text-white"
            dataCY="sycai-assessment-close"
            onClick={close}
          >
            Save &amp; Close
          </FooterAction.Secondary>

          {/* M5.5 round 2 R-I. develop's confirm here is the same green as
              OrganReviewDialog.tsx's; see that file for the ramp and the scope limit. */}
          <FooterAction.Primary
            className="flex-1 bg-green-500 text-sm hover:bg-green-500/90 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            dataCY="sycai-assessment-submit"
            onClick={submitIfEnabled}
            aria-disabled={isSubmitDisabled || undefined}
          >
            <Icons.StatusSuccess
              aria-hidden="true"
              className="mr-1 h-4 w-4"
            />
            Submit
          </FooterAction.Primary>
        </FooterAction>
      </DialogContent>
    </Dialog>
  );
}
